import { useRef } from 'react';
import ChartWrapper, {
  ChartWrapperRef,
} from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { shallowEqual, useSelector } from 'react-redux';
import { COMPANY_INTRINSIC_VALUE_CALC } from '@/constants/feedbackSections';
import { selectDcfData } from '../..';
import { DCFRawDataModal } from '@/pages/CompanyReport/components/Value/';
import loadable from '@loadable/component';
import { Button, Link } from '@simplywallst/ui-core/next';
import {
  useValuationTranslation,
  ValuationTranslation,
} from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import { useLearningContent } from '@/pages/CompanyReport/hooks/useLearningContent';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { getPath } from '@/router/utils';
import { ROUTE_NARRATIVE_COMMUNITY } from '@/constants/routes';
import { getCompanyUrlSegments } from '@/pages/Narrative/utils/getNarrativeUrl';
import { NarrativeCardCompactSynthetic } from '@/pages/Narrative/components/NarrativeCardCompactSynthetic';
import { SWS_HOST } from '@/constants';

const DCF = loadable(() => import('@components/DCF'), { ssr: false });
const LearningTile = loadable(
  () => import('@/components/LearningHubActionSheet'),
  {
    resolveComponent: (c) => c.LearningTile,
  }
);

const HelpContent = () => {
  return (
    <div>
      <ValuationTranslation i18nKey="sharePrice.help.description">
        <p>
          The discounted cash flow is a fundamental valuation technique to
          estimate intrinsic value. The technique involves forecasting how a
          company might grow and what that means for the free cash flow of the
          business.
        </p>
        <p>
          It then uses a discount rate to represent those future cash flows in
          today’s terms. This accounts for the fact that our estimations of
          future cash flows are risky. Higher discount rates indicate higher
          risk.
        </p>
        <p>Depending on the company, we use a different DCF method.</p>
        <p>
          You can read more about our methodology and see how the model works in
          our
          <Link
            target="_blank"
            href={`${SWS_HOST}/help/articles/hc/en-us/articles/4751563581071-Understanding-the-Valuation-section-in-the-company-report#Discounted-Cash-Flow`}
            rel="noopener noreferrer"
          >
            Help Centre.
          </Link>
        </p>
      </ValuationTranslation>
    </div>
  );
};

export const SharePriceVsFairValue = () => {
  const ChartWrapperRef = useRef<ChartWrapperRef>(null);

  const { isCardDismissed, handleDismiss, contentKey } =
    useLearningContent('valuation-dcf');

  const dcfData = useSelector<
    Parameters<typeof selectDcfData>[0],
    ReturnType<typeof selectDcfData>
  >(selectDcfData, shallowEqual);

  const canonicalUrl = useCompanyCanonicalUrl();
  const t = useValuationTranslation();
  const urlSegments = getCompanyUrlSegments(canonicalUrl);

  return (
    <>
      <ChartWrapper
        ref={ChartWrapperRef}
        id="dcf-chart"
        chart={
          dcfData.intrinsicValue <= 0 ? (
            <DCF
              sharePrice={50}
              sharePriceFormatted={''}
              intrinsicValue={60}
              intrinsicValueFormatted={''}
              intrinsicDiscount={0.5}
              intrinsicDiscountFormatted={''}
              showNewStyle={true}
              showEmptyState
              labels={{
                currentValue: t('sharePrice.currentValue'),
                fairValue: t('sharePrice.fairValue'),
                axisUndervalued: t('sharePrice.axis.undervalued'),
                axisAboutRight: t('sharePrice.axis.aboutRight'),
                axisOvervalued: t('sharePrice.axis.overvalued'),
                overvalued: t('sharePrice.overvalued'),
                undervalued: t('sharePrice.undervalued'),
              }}
            />
          ) : (
            <DCF
              sharePrice={dcfData.sharePrice}
              sharePriceFormatted={dcfData.sharePriceFormatted}
              intrinsicValue={dcfData.intrinsicValue}
              intrinsicValueFormatted={dcfData.intrinsicValueFormatted}
              intrinsicDiscount={dcfData.intrinsicDiscount}
              intrinsicDiscountFormatted={dcfData.intrinsicDiscountFormatted}
              showNewStyle={true}
              labels={{
                currentValue: t('sharePrice.currentValue'),
                fairValue: t('sharePrice.fairValue'),
                axisUndervalued: t('sharePrice.axis.undervalued'),
                axisAboutRight: t('sharePrice.axis.aboutRight'),
                axisOvervalued: t('sharePrice.axis.overvalued'),
                overvalued: t('sharePrice.overvalued'),
                undervalued: t('sharePrice.undervalued'),
              }}
            />
          )
        }
        help={<HelpContent />}
        helpTitle={t('sharePrice.help.title')}
        rawData={
          dcfData.intrinsicValue > 0 ? (
            <DCFRawDataModal
              onClose={() => ChartWrapperRef.current?.showChart(true)}
              companyId={dcfData.companyId}
            />
          ) : undefined
        }
        preRenderHeight={380}
        preRenderHeightMdDown={370}
        actionsMarginTop={8}
        feedbackLabel={COMPANY_INTRINSIC_VALUE_CALC}
        moreActions={
          <Button
            variant="outline"
            size="medium"
            href={getPath({
              type: ROUTE_NARRATIVE_COMMUNITY,
              payload: {
                country: urlSegments.country,
                industry: urlSegments.industry,
                companyname: urlSegments.companyName,
                exchange_ticker: urlSegments.exchangeTicker,
              },
            })}
          >
            Explore Narratives
          </Button>
        }
      >
        <NarrativeCardCompactSynthetic canonicalUrl={canonicalUrl} type="DCF" />
      </ChartWrapper>

      {!isCardDismissed ? (
        <LearningTile contentKey={contentKey} onClose={handleDismiss} />
      ) : null}
    </>
  );
};
